import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import Icon from 'components/Icon'
import Img from 'components/Img'
import Type from 'components/Type'
import Button from 'components/Button'
import ModalFlow from 'components/ModalFlow'
import TextButton from 'components/TextButton'
import AllSet from 'components/ModalViews/AllSet'
import PlaidLink from 'components/PlaidLink'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import {
  GET_AUTHORIZATION_TEXT,
  GET_PLAID_ACCESS_TOKEN,
} from 'api/constants'
import { useGet, useTypeLoad, useAgreements } from 'utils/hooks'
import { useAction } from 'utils/actions'
import { isEmpty } from 'lodash'

const PlaidAuthorization = ({
  isCorporate,
  onClose,
}) => {
  const {
    authLanguage,
    onNext,
  } = useAgreements({
    type: 'auto_reward',
    onSuccess: acceptance_id => {},
  })
  const user = useSelector(({ global }) => global.user)
  const company = useSelector(({ global }) => global.company)
  const { closeModal } = useAction()
  const authorizationText = useGet(GET_AUTHORIZATION_TEXT, 'authorizationText')
  const accessTokenLoading = useTypeLoad(GET_PLAID_ACCESS_TOKEN)
  const authTextLoading = useTypeLoad(GET_AUTHORIZATION_TEXT)
  const startingIndex = isCorporate && company.vendor === 1 ? 0 : !isCorporate && user.vendor === 1 ? 1 : 2
  const [activeIndex, setActiveIndex] = useState(startingIndex)

  useEffect(() => {
    if (activeIndex === 3) onClose?.()
  }, [activeIndex])

  return (
    <ModalFlow 
      activeIndex={activeIndex} 
      setActiveIndex={setActiveIndex}
      sizeX="md"
    >
      {/* CORPORATE AUTH */}
      <Stack
        pt={6}
        pb={3}
        px={3}
        textAlign="center"
      >
        <Box
          display="grid"
          justifyContent="center"
          mb={3}
        >
          <Img
            src="/public/large_bank.png"
            height="250px"
          />
        </Box>
        <Type
          variant="nameplateSecondary"
          mb={2}
        >
          Link a corporate account to fund rewards
        </Type>
        <Type
          variant="bodyTall"
          color="neutralCool_700"
          mb={3}
        >
          {authLanguage}
        </Type>
        {activeIndex === 0 && (
          <Button
            justifyContent="center"
            disabled={!authLanguage?.length}
            onClick={onNext}
          >
            <PlaidLink
              isCorporate={isCorporate}
              onSuccess={() => {
                setActiveIndex(3)
                onClose?.()
              }}
            >
              Connect an Account
            </PlaidLink>
          </Button>
        )}
      </Stack>
      {/* PLAID EXPLAINER */}
      <Stack display="grid" gap={3} p={3}>
        <Stack display="grid" gap={2} justifyItems="center">
          <Box
            maxWidth="150px"
            width="100%"
          >
            <Icon
              icon="LogoPlaid"
            />
          </Box>
          <Stack
            gap={3}
          >
            <Type 
              variant="sectionLabel"
              textAlign="center"
            >
              Dónde uses Plaid to link your bank account
            </Type>
            <Stack
              display="grid"
              gap={1}
              gridTemplateColumns="auto 1fr"
            >
              <Icon
                icon="SuccessSmallDuo"
              />
              <Stack
                gap={.5}
              >
                <Type
                  variant="bodyMediumBoldSpaced"
                >
                  Secure
                </Type>
                <Type
                  variant="dataSmall"
                  color="neutralCool_700"
                >
                  Transfer of your information is encrypted end-to-end
                </Type>
              </Stack>
            </Stack>
            <Stack
              display="grid"
              gap={1}
              gridTemplateColumns="auto 1fr"
            >
              <Icon
                icon="SuccessSmallDuo"
              />
              <Stack
                gap={.5}
              >
                <Type
                  variant="bodyMediumBoldSpaced"
                >
                  Private
                </Type>
                <Type
                  variant="dataSmall"
                  color="neutralCool_700"
                >
                  Your personal credentials will never be made accessible to Dónde
                </Type>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        {activeIndex === 1 && (
          <PlaidLink
            isCorporate={isCorporate}
            onSuccess={() => {
              setActiveIndex(3)
              onClose?.()
            }}
          >
            <Button
              justifyContent="center"
              outlined
            >
              Continue
            </Button>
          </PlaidLink>
        )}
      </Stack>
      {/* PLAID AUTH */}
      <Stack
        display="grid"
        gap={3}
        p={3}
        alignItems="space-between"
        alignContent="space-between"
        textAlign="center"
        minHeight="300px"
      >
        <Stack display="grid" gap={2} justifyItems="center">
          <Type variant="sectionLabel">Authorization</Type>
          <Type variant="dataSmall" color="neutralCool_700">{authorizationText.bodyText}</Type>
        </Stack>
        <Stack
          sx={theme => theme.internalMixins.columns({ gap: 2, justify: 'space-between' })}
        >
          {!isEmpty(authorizationText) && activeIndex === 2 && (
            <PlaidLink
              isCorporate={isCorporate}
              onSuccess={() => {
                setActiveIndex(3)
                onClose?.()
              }}
            >
              <Button
                disabled={accessTokenLoading.isLoading || authTextLoading.isLoading}
                // onClick={() => setActiveIndex(1)}
              >
                Agree &amp; Continue
              </Button>
            </PlaidLink>
          )}
          <TextButton
            color="neutralCool_700"
            onMouseUp={closeModal}
          >
            Cancel
          </TextButton>
        </Stack>
      </Stack>
      {/* COMPLETE */}
      <AllSet />
    </ModalFlow>
  )
}

PlaidAuthorization.propTypes = {
  isCorporate: PropTypes.bool,
  onClose: PropTypes.func,
}

export default PlaidAuthorization
